<template>
  <div
    class="tw-transition tw-border tw-border-gray tw-group dark:tw-border-dm-gray tw-rounded-2xl hover:tw-border-secondary dark:hover:tw-border-dm-secondary tw-cursor-pointer"
    :class="isAdding ? 'tw-border-secondary dark:tw-border-dm-secondary' : ''"
  >
    <button
      v-if="!isAdding"
      class="tw-flex tw-items-center tw-w-full tw-text-gray-dark tw-p-4 tw-transition group-hover:tw-text-dark"
      @click="isAdding = true"
    >
      <icon-plus class="tw-w-4 tw-h-4 tw-mr-2" />
      <strong class="tw-font-medium tw-leading-5 tw-tracking-wider">
        {{ $t('foreigners.permits.add_custom_document') }}
      </strong>
    </button>

    <div
      v-if="isAdding"
      class="tw-p-5"
    >
      <el-form
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-3"
        @submit.native.prevent="submitForm"
      >
        <el-form-item
          :label="$t('foreigners.permits.document_name')"
          prop="document_type_name"
        >
          <el-input
            v-model="form.document_type_name"
          />
        </el-form-item>

        <div class="tw-flex tw-gap-5">
          <i-upload
            :value="fileUploadInputValue"
            class="tw-w-1/2"
            multiple
            @changed="filesChanged"
          />

          <el-form-item
            :label="$t('common.filters.status')"
            class="tw-w-1/2"
          >
            <el-select
              v-model="form.status"
              filterable
              placeholder=""
            >
              <el-option
                v-for="item in foreignerPermitDocumentStatuses"
                :key="item.id"
                :label="$t(item.name)"
                :value="item.id"
              >
                <i-badge
                  :type="statusTypes[item.color]"
                  size="small"
                  is-circle
                  class="tw-px-2 tw-ml-1 tw-mr-3"
                />
                {{ $t(item.name) }}
              </el-option>
            </el-select>
          </el-form-item>
        </div>

        <el-form-item
          :label="$t('foreigners.permits.note')"
          prop="note"
        >
          <el-input
            v-model="form.note"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 5 }"
          />
        </el-form-item>

        <div class="tw-flex tw-justify-between tw-items-center">
          <div class="tw-flex tw-gap-5 tw-items-center">
            <el-switch
              v-model="form.editable_by_foreigner"
              :active-text="$t('foreigners.permits.editable_foreigner')"
            />
            <el-switch
              v-model="form.editable_by_client"
              :active-text="$t('foreigners.permits.editable_client')"
            />
          </div>

          <div class="tw-flex tw-gap-3 tw-items-center">
            <i-button @click="isAdding = false">
              {{ $t('common.actions.cancel') }}
            </i-button>
            <i-button
              type="primary"
              native-type="submit"
              :is-loading="isLoading"
            >
              {{ $t('common.actions.save') }}
            </i-button>
          </div>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import { fileToBase64, getValidationError } from '@/helpers';
import { mapState } from 'vuex';

export default {
  props: {
    permit: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isAdding: false,
      isLoading: false,
      form: {
        document_type_name: '',
        note: '',
        files: [],
        status: 10,
        editable_by_foreigner: false,
        editable_by_client: false,
      },
      formRules: {
        document_type_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
      },
      statusTypes: {
        yellow: 'warning',
        red: 'primary',
        green: 'success',
      },
    };
  },

  computed: {
    ...mapState({
      foreignerPermitDocumentStatuses: (state) => state.v2.shared.enums.enums.permit_document_statuses.map((status) => {
        let color = 'red';
        if (status.id === 20) {
          color = 'yellow';
        } else if (status.id === 30) {
          color = 'green';
        }
        return ({
          ...status,
          color,
        });
      }),
    }),
    status() {
      const status = this.$store.getters['v2/shared/enums/getForeignerPermitDocumentStatusById'](this.permitDocument.status);
      return {
        title: this.$t(status['key']),
        type: this.statusTypes[status.color],
      };
    },
    fileUploadInputValue() {
      if (this.form.files?.length > 1) return `${this.$tc('common.field.files_uploaded_label', this.form.files.length )}`;
      return this.form.files?.[0]?.name;
    },
  },

  methods: {
    async filesChanged(files) {
      this.form.files = await Promise.all(files.map(async(source) => {
        const file = await fileToBase64(source).catch(() => null);
        return {
          file,
          name: source.name,
        };
      }));
    },
    async submitForm() {
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      try {
        await this.$store.dispatch('v2/admin/foreigners/foreigner/addPermitDocument', {
          permitId: this.permit.id,
          foreignerId: this.$store.state.v2.admin.foreigners.foreigner.foreigner.id,
          payload: this.form,
        });

        this.isAdding = false;
      } catch (error) {
        const errorMessage = getValidationError(error);

        this.$message.error({
          message: errorMessage ?? this.$capitalize(this.$t('core.error.internal_server_error')),
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
    },
  },
};
</script>
